import { calendar, dayDetail } from './calendar';
import { compareContext, rangeOptions, RANGE_KEYS } from './compare';
import { fromMonthIndex, monthIndex, monthOf } from './dates';
import { deltaColor } from './format';
import { METRICS } from './palette';
import { energyScreen } from './metrics/energy';
import { periodsScreen } from './metrics/periods';
import { cravingsScreen, pmsScreen } from './metrics/windowMetrics';

// Composes one metric tab, as the design's concept()/screen() do. Every
// compare range is built, so a section shows its "Compare with" switch only
// when the ranges would actually give different results (previous cycle vs
// 1 year).

const SCREENS = { periods: periodsScreen, pms: pmsScreen, energy: energyScreen, cravings: cravingsScreen };

/** A section's data with its range wording ("Last 12 months") removed, so only real differences count. */
function signature(section, x) {
  return [x.rl, x.rl.toLowerCase()].reduce((s, label) => s.split(label).join(''), JSON.stringify(section ?? null));
}

/** Colour the KPI notes: deltas red/green (energy: up is better), others grey. */
function colourKpis(kpis, metric) {
  const worseUp = metric !== 'energy';
  return kpis.map((q, i) => {
    const isDelta = /^[↑↓]/.test(q.note || '');
    return {
      ...q,
      noteFg: deltaColor(q.note, worseUp, null) || (i === 0 ? '#3a3a3a' : '#8a8a8a'),
      noteW: isDelta ? 600 : 400,
    };
  });
}

/** Months the calendar may show: from the first fetched day to today. */
export function monthBounds(ctx) {
  return { min: monthIndex(monthOf(ctx.historyStart)), max: monthIndex(monthOf(ctx.today)) };
}

/**
 * @param {ReturnType<import('./context').buildContext>} ctx
 * @param {{
 *   tab: string,
 *   ranges: Record<string, string>,  // `${tab}-${sectionId}` → range key
 *   open: Record<string, boolean>,   // `${tab}-${detailId}` → open
 *   month: number|undefined,         // month index for this tab's calendar
 *   selected: string|undefined,      // selected day for this tab
 * }} state
 */
export function buildView(ctx, { tab, ranges = {}, open = {}, month, selected }) {
  const metric = METRICS.find((m) => m.id === tab) || METRICS[0];
  const S = {};
  RANGE_KEYS.forEach((k) => {
    const x = compareContext(ctx, k);
    S[k] = { x, ...SCREENS[metric.id](ctx, x) };
  });
  const rangeOf = (id) => ranges[`${metric.id}-${id}`] || 'prev';
  const switchFor = (id, show) => ({ show, opts: rangeOptions(rangeOf(id)), note: S[rangeOf(id)].x.rnote, sectionId: id });
  const same = (pick) => signature(pick(S.prev), S.prev.x) === signature(pick(S['1y']), S['1y'].x);

  const base = S.prev;
  const kpis = colourKpis(base.kpis, metric.id);

  const bounds = monthBounds(ctx);
  const monthIdx = Math.min(bounds.max, Math.max(bounds.min, month ?? bounds.max));
  const day = selected || ctx.today;

  return {
    primary: kpis[0],
    secondary: kpis.slice(1),
    lists: base.lists,
    trend: S[rangeOf('trend')].trend,
    trendTf: switchFor('trend', !same((s) => s.trend)),
    details: base.details.map((d, i) => {
      const id = `d${i}`;
      const isOpen = open[`${metric.id}-${d.id}`] ?? d.defaultOpen;
      return { ...S[rangeOf(id)].details[i], isOpen, tf: switchFor(id, !same((s) => s.details[i])) };
    }),
    cal: calendar(ctx, metric.id, fromMonthIndex(monthIdx), day, { canPrev: monthIdx > bounds.min, canNext: monthIdx < bounds.max }),
    monthIndex: monthIdx,
    detail: dayDetail(ctx, metric.id, day),
  };
}
